import React, { ChangeEvent, useState, memo } from 'react';
import {
    Box,
    Grid,
    Paper,
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { useWindowDimensions } from '@/hooks/general';
import { ScreenType } from '@/types/enums/media';
import { Pagination } from '../Pagination/Pagination';

import type { Phrase } from '@/types/types';

import styles from './mobileTableStyles.module.css';
import { chunk } from '@/utils/fp';

export type TableCell = {
    label: string | React.ReactElement | Phrase;
    key: string;
    fullwidth?: boolean;
};

interface TableProps {
    tableData: {
        details: {
            ['aria-label']: string;
        };
        headers: TableCell[];
        rows: {
            values: TableCell[];
            isHighlighted?: boolean;
            key: string;
        }[];
    };
    paginationLimit?: number;
    paginationVisible?: boolean;
}

const DEFAULT_LIMIT = 10;

const Table = ({
    tableData,
    paginationLimit = DEFAULT_LIMIT,
    paginationVisible = true,
}: TableProps) => {
    const totalRows = tableData.rows.length;
    const [prevTotalRows, setPrevTotalRows] = useState(totalRows);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const { screenType } = useWindowDimensions();
    const paginatedRows = chunk(tableData.rows, paginationLimit);
    const pageCount = paginatedRows.length;
    const hasData = currentPage <= pageCount;
    const currentPageData = hasData ? paginatedRows[currentPage - 1] : (paginatedRows[1] ?? []);

    const resetToFirstPageOnDataChange = () => {
        if (prevTotalRows !== totalRows) {
            setPrevTotalRows(totalRows);
            setCurrentPage(1);
        }
    };

    resetToFirstPageOnDataChange();

    const handleChangePage = (_: ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage);
    };

    return screenType === ScreenType.Mobile || screenType === ScreenType.Tablet ? (
        <>
            {currentPageData.map((row) => (
                <Box key={row.key} className={styles.mobileTable} sx={{ mb: 2 }}>
                    <Paper sx={{ p: 2 }}>
                        {row.values.map((cell, idx) => (
                            <Grid
                                key={cell.key}
                                container
                                spacing={1}
                                className={`${styles.row} ${styles.projectNumberRow}`}
                            >
                                <Grid size={cell?.fullwidth ? 12 : 6}>
                                    <div className={styles.title}>
                                        {tableData.headers[idx].label}
                                    </div>
                                </Grid>
                                <Grid
                                    size={cell?.fullwidth ? 12 : 6}
                                    sx={{
                                        justifyContent: cell?.fullwidth ? 'flex-start' : 'flex-end',
                                    }}
                                >
                                    <Box
                                        className={styles.content}
                                        sx={{
                                            alignItems: cell?.fullwidth ? 'flex-start' : 'flex-end',
                                        }}
                                    >
                                        {cell.label}
                                    </Box>
                                </Grid>
                            </Grid>
                        ))}
                    </Paper>
                </Box>
            ))}
            <Grid
                size={12}
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
            >
                {paginationVisible && (
                    <Pagination count={pageCount} page={currentPage} onChange={handleChangePage} />
                )}
            </Grid>
        </>
    ) : (
        <TableContainer>
            <MuiTable {...tableData.details}>
                <TableHead>
                    <TableRow>
                        {tableData.headers.map((th) => (
                            <TableCell key={th.key}>{th.label}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {currentPageData.map((tr) => (
                        <TableRow className={tr.isHighlighted ? 'selected' : ''} key={tr.key}>
                            {tr.values.map((td) => (
                                <TableCell key={td.key}>{td.label}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </MuiTable>
            {paginationVisible && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Pagination count={pageCount} page={currentPage} onChange={handleChangePage} />
                </div>
            )}
        </TableContainer>
    );
};

export default memo(Table);
